import { Icon, IIconStyles, ITooltipHostProps, makeStyles, mergeStyleSets, TooltipHost } from '@fluentui/react';
import { useId } from '@fluentui/react-components';
import * as React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { AppSemanticColor } from '../../themes/app-semantic-colors';
import { getSemanticColor } from '../../utilities/styles';

export interface InfoTooltipProps extends ITooltipHostProps {
    iconAriaLabel?: string;
    iconStyles?: IIconStyles;
    iconTabIndex?: number;
    disabled?: boolean;
}

const messages = defineMessages({
    iconAriaLabel: {
        id: 'InfoTooltip_Icon_AriaLabel',
        defaultMessage: 'More information',
        description: 'Accessible name for an information tooltip icon',
    },
});

/**
 * Style Section
 */

const useDefaultTooltipHostStyles = makeStyles({
    root: {
        display: 'flex',
    },
});

const defaultIconStylesFactory = (disabled: boolean | undefined) =>
    makeStyles((theme) => ({
        root: {
            color: disabled ? getSemanticColor(theme, AppSemanticColor.disabledText) : undefined,
            cursor: 'default',
        },
    }));

/* END */

export const InfoTooltip: React.FC<InfoTooltipProps> = (props: InfoTooltipProps) => {
    const {
        iconAriaLabel,
        iconStyles: providedIconStyles,
        iconTabIndex,
        styles: providedTooltipHostStyles,
        disabled,
        content: providedContent,
        ...tooltipHostProps
    } = props;

    // Intl hooks
    const { formatMessage } = useIntl();

    // Style hooks
    const useDefaultIconStyles = defaultIconStylesFactory(disabled);
    const defaultIconStyles = useDefaultIconStyles();
    const defaultTooltipHostStyles = useDefaultTooltipHostStyles();

    // Memoized data
    const iconStyles = React.useMemo(
        () => mergeStyleSets(defaultIconStyles, providedIconStyles),
        [defaultIconStyles, providedIconStyles]
    );

    const id = useId('tooltip-host');

    const tooltipHostStyles = React.useMemo(
        () => mergeStyleSets(providedTooltipHostStyles, defaultTooltipHostStyles),
        [defaultTooltipHostStyles, providedTooltipHostStyles]
    );

    const content = React.useMemo(() => (disabled ? undefined : providedContent), [disabled, providedContent]);

    return (
        <TooltipHost {...tooltipHostProps} id={id} content={content} styles={tooltipHostStyles}>
            <Icon
                aria-label={iconAriaLabel ?? formatMessage(messages.iconAriaLabel)}
                aria-describedby={id}
                role="img"
                aria-hidden="false"
                iconName="Info"
                styles={iconStyles}
                tabIndex={iconTabIndex || 0}
            />
        </TooltipHost>
    );
};

export default InfoTooltip;
